"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import {
	RiHome2Line,
	RiHome2Fill,
	RiHome3Fill,
	RiHome3Line,
} from "@remixicon/react";
import {
	HmcButton,
	HmcCard,
	HmcContainerHeader,
} from "@adi-ctx/harmonic-core-components-react";
import { Link } from "@/components";
import type { TCard } from "@/components/common/Card/card.types";
import { t } from "@/utils";
import { HmcIcon } from "@/components/hmc-on-server";
import type { TPropsWithOptionalChildren } from "@/types";

type CardProps = {
	className?: string;
	card?: TCard;
};

export default function Card({
	card,
	children,
	className,
}: TPropsWithOptionalChildren<CardProps>) {
	const headerRef = useRef<HTMLHmcContainerHeaderElement | null>(null);
	const [isFooterActionInProgress, setIsFooterActionInProgress] =
		useState(false);

	const onFooterAction = useCallback(async (action: any) => {
		setIsFooterActionInProgress(true);
		await action();
		setIsFooterActionInProgress(false);
	}, []);

	useEffect(() => {
		headerRef.current?.shadowRoot?.append(
			Object.assign(document.createElement("style"), {
				// Forcing titles and subtitles to just 1 line
				innerText:
					".title-group { width: 100% }" +
					".heading, .subtitle {" +
					"display: block!important; " +
					"white-space: nowrap; " +
					"text-overflow: ellipsis;" +
					"overflow: hidden;" +
					"width: 100%" +
					"}",
			})
		);
	}, []);

	if (!card) return;

	return (
		<HmcCard density="high" data-testid={`card-${card.title}`}>
			{card.title && (
				<HmcContainerHeader
					ref={headerRef}
					slot="card-header"
					density="high"
					heading={card.title}
					subtitle={card.subtitle}
					data-testid="card-header"
					className="block w-full"
				/>
			)}

			<div className="relative">
				{card.image && (
					<Image
						width="0"
						height={card.imageHeight ?? 0}
						sizes="100vw"
						className="w-full object-cover"
						style={{ height: card.imageHeight }}
						src={card.image}
						alt={card.title ?? card.description!}
						data-testid="card-image"
					/>
				)}
				{card.description && (
					<p className="truncate-2-lines-body-xs hmc-body-xs  mt-4">
						{card.description}
					</p>
				)}
			</div>

			{children}

			{(card.link ??
				card.onEdit ??
				card.onDelete ??
				card.onHomeOn ??
				card.onHomeFeaturedOn) && (
				<div slot="card-footer" className="flex items-center justify-end gap-2">
					{isFooterActionInProgress && (
						<span data-testid="card-action-in-progress">
							{t("general.action-in-progress-text")}
						</span>
					)}

					{card.link && (
						<Link
							href={card.link.url}
							title={card.title}
							data-testid="card-link"
						>
							<HmcButton kind="secondary" size="sm">
								{card.link.title}
							</HmcButton>
						</Link>
					)}

					{card.onHomeOn && (
						<HmcButton
							isIconOnly
							isDisabled={isFooterActionInProgress}
							kind="tertiary"
							className="hover:cursor-pointer"
							title={t("pages.cms.action-homepage-hide-title")}
							data-testid="card-action-homepage-hide-button"
							size="sm"
							onClick={async () => {
								await onFooterAction(card.onHomeOn);
							}}
						>
							<RiHome2Fill data-testid="card-action-homepage-hide-button-icon" />
						</HmcButton>
					)}

					{card.onHomeOff && (
						<HmcButton
							isIconOnly
							isDisabled={isFooterActionInProgress}
							kind="tertiary"
							className="hover:cursor-pointer"
							title={t("pages.cms.action-homepage-show-title")}
							data-testid="card-action-homepage-show-button"
							size="sm"
							onClick={async () => {
								await onFooterAction(card.onHomeOff);
							}}
						>
							<RiHome2Line data-testid="card-action-homepage-show-button-icon" />
						</HmcButton>
					)}

					{card.onHomeFeaturedOn && (
						<HmcButton
							isIconOnly
							isDisabled={isFooterActionInProgress}
							kind="tertiary"
							className="hover:cursor-pointer"
							title={t("pages.cms.action-homepage-featured-hide-title")}
							data-testid="card-action-homepage-featured-hide-button"
							size="sm"
							onClick={async () => {
								await onFooterAction(card.onHomeFeaturedOn);
							}}
						>
							<RiHome3Fill data-testid="card-action-homepage-featured-hide-button-icon" />
						</HmcButton>
					)}

					{card.onHomeFeaturedOff && (
						<HmcButton
							isIconOnly
							isDisabled={isFooterActionInProgress}
							kind="tertiary"
							className="hover:cursor-pointer"
							title={t("pages.cms.action-homepage-featured-show-title")}
							data-testid="card-action-homepage-featured-show-button"
							size="sm"
							onClick={async () => {
								await onFooterAction(card.onHomeFeaturedOff);
							}}
						>
							<RiHome3Line data-testid="card-action-homepage-featured-show-button-icon" />
						</HmcButton>
					)}

					{card.onDelete && (
						<HmcButton
							isIconOnly
							isDisabled={isFooterActionInProgress}
							kind="tertiary"
							className="hover:cursor-pointer"
							title={t("pages.cms.action-delete-title")}
							data-testid="card-action-delete-button"
							size="sm"
							onClick={async () => {
								await onFooterAction(card.onDelete);
							}}
						>
							<HmcIcon
								name="delete-m"
								data-testid="card-action-delete-button-icon"
							/>
						</HmcButton>
					)}

					{card.onEdit && (
						<HmcButton
							isIconOnly
							isDisabled={isFooterActionInProgress}
							kind="secondary"
							className="hover:cursor-pointer"
							title={t("pages.cms.action-edit-title")}
							data-testid="card-action-edit-button"
							size="sm"
							onClick={card.onEdit}
						>
							<HmcIcon
								name="edit-m"
								data-testid="card-action-edit-button-icon"
							/>
						</HmcButton>
					)}
				</div>
			)}
		</HmcCard>
	);
}
